import React, { useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const VARIABLES = [
  { key: 'client_name', label: 'Nom du client', category: 'Client' },
  { key: 'client_email', label: 'Email du client', category: 'Client' },
  { key: 'client_phone', label: 'Téléphone du client', category: 'Client' },
  { key: 'status', label: 'Statut', category: 'Client' },
  { key: 'sender_name', label: "Nom de l'expéditeur", category: 'Expéditeur' },
  { key: 'sender_email', label: "Email de l'expéditeur", category: 'Expéditeur' },
  { key: 'date', label: 'Date du jour', category: 'Système' },
  { key: 'company_name', label: "Nom de l'entreprise", category: 'Système' },
];

const VARIABLE_COLORS = {
  'Client': { bg: 'bg-blue-500/15', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-500/30' },
  'Expéditeur': { bg: 'bg-purple-500/15', text: 'text-purple-600 dark:text-purple-400', border: 'border-purple-500/30' },
  'Système': { bg: 'bg-green-500/15', text: 'text-green-600 dark:text-green-400', border: 'border-green-500/30' },
};

export const VariableHighlighter = ({ text, className = '' }) => {
  if (!text) return null;
  const parts = text.split(/(\{[a-zA-Z_]+\})/g);
  return (
    <span className={className}>
      {parts.map((part, index) => {
        const match = part.match(/^\{([a-zA-Z_]+)\}$/);
        if (match) {
          const varKey = match[1];
          const variable = VARIABLES.find(v => v.key === varKey);
          const colors = variable ? VARIABLE_COLORS[variable.category] : VARIABLE_COLORS['Système'];
          return (
            <span
              key={index}
              className={`inline-flex items-center px-2 py-0.5 mx-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}
            >
              {variable ? variable.label : varKey}
            </span>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </span>
  );
};

export const VariableSelector = ({ onSelect, buttonText = 'Insérer une variable' }) => {
  const groupedVariables = VARIABLES.reduce((acc, v) => {
    if (!acc[v.category]) acc[v.category] = [];
    acc[v.category].push(v);
    return acc;
  }, {});

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1" data-testid="variable-selector">
          <span className="text-xs">{buttonText}</span>
          <ChevronDown size={14} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {Object.entries(groupedVariables).map(([category, vars], idx) => (
          <React.Fragment key={category}>
            {idx > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs text-muted-foreground">{category}</DropdownMenuLabel>
            {vars.map((variable) => {
              const colors = VARIABLE_COLORS[category];
              return (
                <DropdownMenuItem
                  key={variable.key}
                  onClick={() => onSelect(`{${variable.key}}`)}
                  className="cursor-pointer"
                >
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} mr-2`}>
                    {`{${variable.key}}`}
                  </span>
                  <span className="text-sm">{variable.label}</span>
                </DropdownMenuItem>
              );
            })}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const SmsEditorWithTags = ({ value, onChange }) => {
  const textareaRef = useRef(null);

  const handleInsertVariable = (variable) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      onChange((value || '') + variable);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = (value || '').substring(0, start) + variable + (value || '').substring(end);
    onChange(newValue);
    setTimeout(() => {
      textarea.focus();
      const newPos = start + variable.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const charCount = (value || '').length;
  const segments = charCount <= 160 ? 1 : Math.ceil(charCount / 153);
  const isOverLimit = charCount > 160;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">Message</span>
        <VariableSelector onSelect={handleInsertVariable} />
      </div>

      {value && value.includes('{') && (
        <div className="p-3 bg-muted/50 rounded-lg border border-border text-sm min-h-[40px]">
          <p className="text-xs text-muted-foreground mb-1">Aperçu :</p>
          <VariableHighlighter text={value} className="leading-relaxed" />
        </div>
      )}

      <textarea
        ref={textareaRef}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Tapez votre message ou utilisez le menu pour insérer des variables..."
        className="w-full min-h-[100px] p-3 border border-border rounded-lg text-sm resize-none bg-background text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary"
        data-testid="sms-template-content"
      />

      <div className="flex items-center justify-between text-xs">
        <span className={isOverLimit ? 'text-amber-500 font-medium' : 'text-muted-foreground'}>
          {charCount} caractères
        </span>
        <span className={isOverLimit ? 'text-amber-500' : 'text-muted-foreground'}>
          {segments} segment{segments > 1 ? 's' : ''}
        </span>
      </div>

      {isOverLimit && (
        <p className="text-xs text-amber-500 bg-amber-500/10 p-2 rounded border border-amber-500/20">
          Les SMS de plus de 160 caractères sont envoyés en plusieurs segments (153 car./segment)
        </p>
      )}
    </div>
  );
};

export const EmailSubjectWithTags = ({ value, onChange }) => {
  const inputRef = useRef(null);

  const handleInsertVariable = (variable) => {
    const input = inputRef.current;
    if (!input) {
      onChange((value || '') + variable);
      return;
    }
    const start = input.selectionStart || (value || '').length;
    const end = input.selectionEnd || (value || '').length;
    const newValue = (value || '').substring(0, start) + variable + (value || '').substring(end);
    onChange(newValue);
    setTimeout(() => {
      input.focus();
      const newPos = start + variable.length;
      input.setSelectionRange(newPos, newPos);
    }, 0);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">Sujet</span>
        <VariableSelector onSelect={handleInsertVariable} buttonText="Variable" />
      </div>

      {value && value.includes('{') && (
        <div className="p-2 bg-muted/50 rounded border border-border text-sm">
          <VariableHighlighter text={value} />
        </div>
      )}

      <input
        ref={inputRef}
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Ex: Bienvenue chez nous, {client_name} !"
        className="w-full p-2 border border-border rounded-lg text-sm bg-background text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary"
        data-testid="email-template-subject"
      />
    </div>
  );
};

export { VARIABLES, VARIABLE_COLORS };
export default VariableSelector;
